/**
 * HousingAssignment - Visual delegate-to-housing-unit assignment UI
 * Drag-and-drop interface for assigning delegates to rooms/units
 * Supports both manual assignment and auto-assignment based on tier priority
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Home, User, Users, Plus, Search, Bed, Building2,
  Loader2, X, Check, Move, GripVertical,
  UserPlus, UserMinus, BedDouble, BedSingle, Wand2,
  AlertCircle, CheckCircle2, XCircle, Star, Crown,
  Edit2, Trash2, Sparkles, MoreVertical
} from 'lucide-react';
import api from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import DelegateAssignPopover from '@/components/accommodation/DelegateAssignPopover';
import BuildingMapView from '@/components/accommodation/BuildingMapView';

const ROOM_TYPES = [
  { id: 'single', name: 'Single Room', icon: BedSingle, capacity: 1 },
  { id: 'double', name: 'Double Room', icon: BedDouble, capacity: 2 },
  { id: 'suite', name: 'Suite', icon: Home, capacity: 4 },
  { id: 'shared', name: 'Shared Dorm', icon: Users, capacity: 6 },
];

const UserCheck = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="16 11 18 13 22 9" />
  </svg>
);

const HousingAssignment = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterUnassigned, setFilterUnassigned] = useState(false);
  const [draggedDelegate, setDraggedDelegate] = useState(null);
  const [isAddingUnit, setIsAddingUnit] = useState(false);
  const [newUnit, setNewUnit] = useState({
    name: '', building: '', floor: '', type: 'double', notes: ''
  });
  
  // Auto-assign dialog state
  const [showAutoAssignDialog, setShowAutoAssignDialog] = useState(false);
  const [autoAssignOptions, setAutoAssignOptions] = useState({
    respect_tier_priority: true,
    only_unassigned: true,
    housing_type_preferences: []
  });
  const [autoAssignResults, setAutoAssignResults] = useState(null);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  
  // Manual assign dialog state
  const [showManualAssignDialog, setShowManualAssignDialog] = useState(false);
  const [manualAssignUnit, setManualAssignUnit] = useState('');
  const [manualAssignDelegate, setManualAssignDelegate] = useState('');

  // Group allocation — select multiple delegates to assign together to one room
  const [selectedDelegateIds, setSelectedDelegateIds] = useState(new Set());
  const toggleDelegateSelection = (id) => {
    setSelectedDelegateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearDelegateSelection = () => setSelectedDelegateIds(new Set());
  
  // Edit unit state
  const [editingUnit, setEditingUnit] = useState(null);
  
  // Grouping and sorting state
  const [groupBy, setGroupBy] = useState('none'); // none, floor, building, status
  const [sortBy, setSortBy] = useState('name'); // name, room_number, capacity, status
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  const [viewMode, setViewMode] = useState('list'); // list | map

  // Fetch housing units
  const { data: units = [], isLoading: loadingUnits } = useQuery({
    queryKey: ['housing-units', eventId],
    queryFn: () => api.get(`/events/${eventId}/housing/units`).then(r => r.data),
  });

  // Fetch delegates
  const { data: delegates = [], isLoading: loadingDelegates } = useQuery({
    queryKey: ['delegates', eventId],
    queryFn: () => api.get(`/events/${eventId}/delegates`).then(r => r.data),
  });

  // Mutations
  const createUnitMutation = useMutation({
    mutationFn: (unit) => api.post(`/events/${eventId}/housing/units`, unit),
    onSuccess: () => {
      toast.success('Housing unit added!');
      queryClient.invalidateQueries(['housing-units', eventId]);
      setIsAddingUnit(false);
      setNewUnit({ name: '', building: '', floor: '', type: 'double', notes: '' });
    },
    onError: () => toast.error('Failed to add unit')
  });

  const assignDelegateMutation = useMutation({
    mutationFn: ({ unitId, delegateId }) => 
      api.post(`/events/${eventId}/housing/units/${unitId}/assign`, { delegate_id: delegateId }),
    onSuccess: () => {
      toast.success('Delegate assigned!');
      queryClient.invalidateQueries(['housing-units', eventId]);
      queryClient.invalidateQueries(['delegates', eventId]);
    },
    onError: () => toast.error('Failed to assign delegate')
  });

  const unassignDelegateMutation = useMutation({
    mutationFn: ({ unitId, delegateId }) => 
      api.post(`/events/${eventId}/housing/units/${unitId}/unassign`, { delegate_id: delegateId }),
    onSuccess: () => {
      toast.success('Delegate unassigned');
      queryClient.invalidateQueries(['housing-units', eventId]);
      queryClient.invalidateQueries(['delegates', eventId]);
    }
  });
  
  const autoAssignMutation = useMutation({
    mutationFn: (options) => api.post(`/admin/events/${eventId}/housing/auto-assign`, options),
    onSuccess: (response) => {
      setAutoAssignResults(response.data);
      setShowAutoAssignDialog(false);
      setShowResultsDialog(true);
      queryClient.invalidateQueries(['housing-units', eventId]);
      queryClient.invalidateQueries(['delegates', eventId]);
      toast.success(response.data.message || 'Auto-assignment complete!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Auto-assignment failed');
    }
  });

  // Update unit mutation
  const updateUnitMutation = useMutation({
    mutationFn: ({ unitId, data }) => api.put(`/events/${eventId}/housing/units/${unitId}`, data),
    onSuccess: () => {
      toast.success('Housing unit updated!');
      queryClient.invalidateQueries(['housing-units', eventId]);
      setEditingUnit(null);
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Failed to update unit')
  });

  // Delete unit mutation
  const deleteUnitMutation = useMutation({
    mutationFn: (unitId) => api.delete(`/events/${eventId}/housing/units/${unitId}`),
    onSuccess: () => {
      toast.success('Housing unit deleted!');
      queryClient.invalidateQueries(['housing-units', eventId]);
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Failed to delete unit')
  });

  // Update status mutation (ready, dirty, maintenance, etc.)
  const updateStatusMutation = useMutation({
    mutationFn: ({ unitId, status, notes }) => 
      api.post(`/events/${eventId}/housing/units/${unitId}/status`, { status, notes }),
    onSuccess: () => {
      toast.success('Status updated!');
      queryClient.invalidateQueries(['housing-units', eventId]);
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Failed to update status')
  });

  // Helper functions
  const getAssignedDelegates = (unitId) => delegates.filter(d => d.housing_unit_id === unitId);
  
  const unassignedDelegates = delegates.filter(d => 
    !d.housing_unit_id && (d.access?.accommodation_access !== false)
  );

  const filteredDelegates = (filterUnassigned ? unassignedDelegates : delegates.filter(d => d.access?.accommodation_access !== false)).filter(d => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      d.name?.toLowerCase().includes(query) ||
      d.first_name?.toLowerCase().includes(query) ||
      d.last_name?.toLowerCase().includes(query) ||
      d.email?.toLowerCase().includes(query) ||
      d.company?.toLowerCase().includes(query)
    );
  });

  const getRoomType = (typeId) => ROOM_TYPES.find(t => t.id === typeId) || ROOM_TYPES[1];

  // Extract room number from name for sorting (e.g., "Room 101" -> 101)
  const extractRoomNumber = (name) => {
    const match = name?.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };

  // Sort units
  const sortUnits = (unitsList) => {
    return [...unitsList].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'room_number':
          comparison = extractRoomNumber(a.name) - extractRoomNumber(b.name);
          break;
        case 'capacity':
          const capA = getRoomType(a.type)?.capacity || 2;
          const capB = getRoomType(b.type)?.capacity || 2;
          comparison = capA - capB;
          break;
        case 'status':
          comparison = (a.status || 'available').localeCompare(b.status || 'available');
          break;
        case 'name':
        default:
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });
  };

  // Group units
  const groupUnits = (unitsList) => {
    if (groupBy === 'none') {
      return { 'All Units': sortUnits(unitsList) };
    }
    
    const grouped = {};
    unitsList.forEach(unit => {
      let key;
      switch (groupBy) {
        case 'floor':
          key = unit.floor ? `Floor ${unit.floor}` : 'No Floor Assigned';
          break;
        case 'building':
          key = unit.building || 'No Building Assigned';
          break;
        case 'status':
          key = (unit.status || 'available').charAt(0).toUpperCase() + (unit.status || 'available').slice(1);
          break;
        default:
          key = 'All Units';
      }
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(unit);
    });
    
    // Sort each group and sort group keys
    const sortedGroups = {};
    Object.keys(grouped).sort((a, b) => {
      // Extract numbers for floor sorting
      if (groupBy === 'floor') {
        const numA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
        const numB = parseInt(b.match(/\d+/)?.[0] || '0', 10);
        return sortOrder === 'desc' ? numB - numA : numA - numB;
      }
      return sortOrder === 'desc' ? b.localeCompare(a) : a.localeCompare(b);
    }).forEach(key => {
      sortedGroups[key] = sortUnits(grouped[key]);
    });
    
    return sortedGroups;
  };

  const groupedUnits = groupUnits(units);

  // Drag handlers
  const handleDragStart = (e, delegate) => {
    setDraggedDelegate(delegate);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, unit) => {
    e.preventDefault();
    if (!draggedDelegate) return;

    // Use the unit's DYNAMIC capacity field, not the hardcoded room-type default
    const capacity = Number(unit.capacity) || Number(ROOM_TYPES.find(t => t.id === unit.type)?.capacity) || 1;
    const currentOccupants = getAssignedDelegates(unit.id).length;

    if (currentOccupants >= capacity) {
      toast.error('This unit is at full capacity');
      setDraggedDelegate(null);
      return;
    }

    if (draggedDelegate.housing_unit_id && draggedDelegate.housing_unit_id !== unit.id) {
      unassignDelegateMutation.mutate({
        unitId: draggedDelegate.housing_unit_id,
        delegateId: draggedDelegate.id
      });
    }

    assignDelegateMutation.mutate({
      unitId: unit.id,
      delegateId: draggedDelegate.id
    });

    setDraggedDelegate(null);
  };

  const handleManualAssign = () => {
    if (!manualAssignUnit || !manualAssignDelegate) {
      toast.error('Please select both a delegate and a room');
      return;
    }
    assignDelegateMutation.mutate({
      unitId: manualAssignUnit,
      delegateId: manualAssignDelegate
    });
    setShowManualAssignDialog(false);
    setManualAssignUnit('');
    setManualAssignDelegate('');
  };

  if (loadingUnits || loadingDelegates) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="housing-assignment">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-indigo-500" />
            Housing Assignment
          </h2>
          <p className="text-slate-500">Assign delegates to rooms manually or automatically</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowAutoAssignDialog(true)} data-testid="auto-assign-btn">
            <Wand2 className="h-4 w-4 mr-2" />
            Auto-Assign
          </Button>
          <Button variant="outline" onClick={() => setShowManualAssignDialog(true)} data-testid="manual-assign-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Quick Assign
          </Button>
          <Dialog open={isAddingUnit} onOpenChange={setIsAddingUnit}>
            <DialogTrigger asChild>
              <Button data-testid="add-unit-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Unit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Housing Unit</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <Input placeholder="Room/Unit name (e.g., Room 101)" value={newUnit.name} onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })} />
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="Building" value={newUnit.building} onChange={(e) => setNewUnit({ ...newUnit, building: e.target.value })} />
                  <Input placeholder="Floor" value={newUnit.floor} onChange={(e) => setNewUnit({ ...newUnit, floor: e.target.value })} />
                </div>
                <Select value={newUnit.type} onValueChange={(v) => setNewUnit({ ...newUnit, type: v })}>
                  <SelectTrigger><SelectValue placeholder="Room type" /></SelectTrigger>
                  <SelectContent>
                    {ROOM_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {type.name} (capacity: {type.capacity})
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Input placeholder="Notes (optional)" value={newUnit.notes} onChange={(e) => setNewUnit({ ...newUnit, notes: e.target.value })} />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsAddingUnit(false)}>Cancel</Button>
                  <Button onClick={() => createUnitMutation.mutate(newUnit)} disabled={!newUnit.name || createUnitMutation.isPending}>Add Unit</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-500" />
              <div>
                <p className="text-2xl font-bold">{units.length}</p>
                <p className="text-xs text-slate-500">Housing Units</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Bed className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{units.reduce((sum, u) => sum + (getRoomType(u.type)?.capacity || 0), 0)}</p>
                <p className="text-xs text-slate-500">Total Capacity</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{delegates.length - unassignedDelegates.length}</p>
                <p className="text-xs text-slate-500">Assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserMinus className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{unassignedDelegates.length}</p>
                <p className="text-xs text-slate-500">Unassigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Delegates List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5" />
              Delegates
            </CardTitle>
            <CardDescription>Drag to assign housing</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search delegates..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={filterUnassigned} onChange={(e) => setFilterUnassigned(e.target.checked)} className="rounded" />
                Show unassigned only ({unassignedDelegates.length})
              </label>
              {/* Group allocation action bar — visible when 2+ delegates are selected */}
              {selectedDelegateIds.size > 0 && (
                <div className="flex items-center gap-2 p-2 border border-purple-200 bg-purple-50 rounded-md" data-testid="group-assign-bar">
                  <span className="text-xs font-medium text-purple-900 flex-1">
                    {selectedDelegateIds.size} selected
                  </span>
                  <button
                    type="button"
                    onClick={clearDelegateSelection}
                    className="text-xs text-slate-600 hover:underline"
                  >
                    Clear
                  </button>
                  <DelegateAssignPopover
                    delegate={null}
                    units={units}
                    delegates={delegates}
                    groupIds={Array.from(selectedDelegateIds)}
                    groupLabel={`Assign ${selectedDelegateIds.size} delegates to the same room`}
                    onAssign={(unitId, ids) => {
                      const list = ids || Array.from(selectedDelegateIds);
                      Promise.all(
                        list.map((did) =>
                          assignDelegateMutation.mutateAsync({ unitId, delegateId: did }).catch(() => null)
                        )
                      ).then(() => {
                        clearDelegateSelection();
                        toast.success(`Assigned ${list.length} delegates to the same room`);
                      });
                    }}
                  />
                </div>
              )}
            </div>

            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {filteredDelegates.map(delegate => (
                  <div
                    key={delegate.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, delegate)}
                    className={`p-3 border rounded-lg cursor-grab active:cursor-grabbing hover:bg-slate-50 transition-colors ${delegate.housing_unit_id ? 'bg-green-50 border-green-200' : ''} ${selectedDelegateIds.has(delegate.id) ? 'ring-2 ring-purple-300' : ''}`}
                    data-testid={`delegate-row-${delegate.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedDelegateIds.has(delegate.id)}
                        onChange={(e) => { e.stopPropagation(); toggleDelegateSelection(delegate.id); }}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded flex-shrink-0"
                        aria-label={`Select ${delegate.first_name || ''} ${delegate.last_name || ''}`}
                        data-testid={`delegate-select-${delegate.id}`}
                      />
                      <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{delegate.first_name} {delegate.last_name}</p>
                        <p className="text-xs text-slate-500 truncate">{delegate.company || delegate.email}</p>
                      </div>
                      <DelegateAssignPopover
                        delegate={delegate}
                        units={units}
                        delegates={delegates}
                        onAssign={(unitId) => assignDelegateMutation.mutate({ unitId, delegateId: delegate.id })}
                        onUnassign={(unitId) => unassignDelegateMutation.mutate({ unitId, delegateId: delegate.id })}
                      />
                    </div>
                  </div>
                ))}
                {filteredDelegates.length === 0 && (
                  <p className="text-center text-slate-500 py-4 text-sm">No delegates found</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Housing Units Grid */}
        <div className="lg:col-span-2 space-y-4">
          {/* Sorting and Grouping Controls */}
          <Card className="p-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500 whitespace-nowrap">Group by:</Label>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger className="w-32 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Grouping</SelectItem>
                    <SelectItem value="floor">Floor</SelectItem>
                    <SelectItem value="building">Building</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500 whitespace-nowrap">Sort by:</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-32 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="room_number">Room Number</SelectItem>
                    <SelectItem value="capacity">Capacity</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              >
                {sortOrder === 'asc' ? '↑ Low to High' : '↓ High to Low'}
              </Button>

              <div className="flex items-center gap-1 border rounded-md p-0.5 bg-slate-50 ml-2">
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`px-2 py-1 text-xs rounded ${viewMode === 'list' ? 'bg-white shadow-sm font-medium' : 'text-slate-500 hover:text-slate-900'}`}
                  data-testid="view-mode-list"
                >
                  List
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('map')}
                  className={`px-2 py-1 text-xs rounded ${viewMode === 'map' ? 'bg-white shadow-sm font-medium' : 'text-slate-500 hover:text-slate-900'}`}
                  data-testid="view-mode-map"
                >
                  Map
                </button>
              </div>

              <div className="ml-auto text-xs text-slate-500">
                {units.length} units total
              </div>
            </div>
          </Card>

          {viewMode === 'map' ? (
            <BuildingMapView
              units={units}
              delegates={delegates}
              onUnitClick={(u) => {
                setManualAssignUnit(u.id);
                setShowManualAssignDialog(true);
              }}
            />
          ) : (
            <>
          {/* Grouped Units Display */}
          {Object.entries(groupedUnits).map(([groupName, groupUnits]) => (
            <div key={groupName} className="space-y-3">
              {groupBy !== 'none' && (
                <div className="flex items-center gap-2 sticky top-0 bg-white py-2 z-10">
                  <div className="h-px flex-1 bg-slate-200" />
                  <Badge variant="outline" className="bg-slate-50 font-semibold">
                    {groupName} ({groupUnits.length})
                  </Badge>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groupUnits.map(unit => {
                  const roomType = getRoomType(unit.type);
                  const Icon = roomType.icon;
                  // Use server-provided assigned_delegates if available, fallback to local filter
                  const assigned = unit.assigned_delegates || getAssignedDelegates(unit.id);
                  const occupancy = unit.occupied ?? assigned.length;
                  // Use unit capacity from database, not hardcoded room type
                  const capacity = unit.capacity || roomType.capacity;
                  const isFull = unit.is_full ?? (occupancy >= capacity);

                  return (
                    <Card
                      key={unit.id}
                      className={`transition-all ${isFull ? 'border-green-300 bg-green-50/50' : 'border-dashed'} ${draggedDelegate && !isFull ? 'ring-2 ring-blue-300 ring-offset-2' : ''}`}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, unit)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              <Icon className="h-5 w-5 text-indigo-500" />
                              {unit.name}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {unit.building && `${unit.building}`}
                              {unit.floor && ` • Floor ${unit.floor}`}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Status Badge */}
                            <Badge 
                              variant="outline" 
                              className={
                                unit.status === 'ready' ? 'bg-green-100 text-green-700 border-green-300' :
                                unit.status === 'dirty' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                unit.status === 'cleaning' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                                unit.status === 'maintenance' ? 'bg-red-100 text-red-700 border-red-300' :
                                unit.status === 'occupied' ? 'bg-purple-100 text-purple-700 border-purple-300' :
                                'bg-slate-100 text-slate-700'
                              }
                            >
                              {unit.status || 'available'}
                            </Badge>
                            <Badge variant={isFull ? 'default' : 'secondary'}>{occupancy}/{capacity}</Badge>
                            
                            {/* Actions Menu */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setEditingUnit({...unit})}>
                                  <Edit2 className="h-4 w-4 mr-2" />
                                  Edit Unit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => updateStatusMutation.mutate({ unitId: unit.id, status: 'ready' })}
                                  className="text-green-600"
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  Mark Ready
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateStatusMutation.mutate({ unitId: unit.id, status: 'dirty' })}
                                  className="text-yellow-600"
                                >
                                  <AlertCircle className="h-4 w-4 mr-2" />
                                  Mark Dirty
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateStatusMutation.mutate({ unitId: unit.id, status: 'cleaning' })}
                                  className="text-blue-600"
                                >
                                  <Sparkles className="h-4 w-4 mr-2" />
                                  Mark Cleaning
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateStatusMutation.mutate({ unitId: unit.id, status: 'maintenance' })}
                                  className="text-red-600"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Mark Maintenance
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    if (unit.assigned_to) {
                                      toast.error('Unassign delegate first before deleting');
                                    } else if (window.confirm(`Delete "${unit.name}"?`)) {
                                      deleteUnitMutation.mutate(unit.id);
                                    }
                                  }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Unit
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 min-h-[80px]">
                          {assigned.map(delegate => {
                            // Handle both formats: server-provided (name) and local filter (first_name/last_name)
                            const displayName = delegate.name || `${delegate.first_name || ''} ${delegate.last_name || ''}`.trim();
                            return (
                              <div key={delegate.id} className="flex items-center justify-between p-2 bg-white border rounded">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-slate-400" />
                                  <span className="text-sm">{displayName}</span>
                                  {delegate.checked_in && (
                                    <Badge variant="outline" className="text-xs bg-green-50">In</Badge>
                                  )}
                                </div>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => unassignDelegateMutation.mutate({ unitId: unit.id, delegateId: delegate.id })}>
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            );
                          })}
                          {occupancy === 0 && (
                            <div className="flex items-center justify-center h-20 border-2 border-dashed rounded-lg text-slate-400">
                              <div className="text-center">
                                <Move className="h-6 w-6 mx-auto mb-1" />
                                <p className="text-xs">Drop delegates here</p>
                              </div>
                            </div>
                          )}
                          {occupancy > 0 && occupancy < capacity && (
                            <div className="flex items-center justify-center p-2 border-2 border-dashed rounded text-slate-400 text-xs">
                              <UserPlus className="h-3 w-3 mr-1" />
                              {capacity - occupancy} spot(s) available
                            </div>
                          )}
                        </div>
                        {unit.notes && <p className="text-xs text-slate-500 mt-2 pt-2 border-t">{unit.notes}</p>}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
          
          {units.length === 0 && (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-600">No Housing Units</h3>
                <p className="text-sm text-slate-500 mt-2">Add housing units to start assigning delegates</p>
              </CardContent>
            </Card>
          )}
            </>
          )}
        </div>
      </div>

      {/* Auto-Assign Dialog */}
      <Dialog open={showAutoAssignDialog} onOpenChange={setShowAutoAssignDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-indigo-500" />
              Auto-Assign Housing
            </DialogTitle>
            <DialogDescription>
              Automatically assign housing to delegates based on tier priority.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="flex items-center gap-2"><Crown className="h-4 w-4 text-amber-500" />Respect Tier Priority</Label>
                <p className="text-xs text-slate-500">VIP and premium tiers get assigned first</p>
              </div>
              <Switch checked={autoAssignOptions.respect_tier_priority} onCheckedChange={(v) => setAutoAssignOptions({...autoAssignOptions, respect_tier_priority: v})} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Only Unassigned Delegates</Label>
                <p className="text-xs text-slate-500">Skip delegates who already have rooms</p>
              </div>
              <Switch checked={autoAssignOptions.only_unassigned} onCheckedChange={(v) => setAutoAssignOptions({...autoAssignOptions, only_unassigned: v})} />
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-sm font-medium mb-2">Assignment Summary</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-slate-600">Unassigned delegates:</div>
                <div className="font-medium">{unassignedDelegates.length}</div>
                <div className="text-slate-600">Available units:</div>
                <div className="font-medium">{units.filter(u => !getAssignedDelegates(u.id).length).length}</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutoAssignDialog(false)}>Cancel</Button>
            <Button onClick={() => autoAssignMutation.mutate(autoAssignOptions)} disabled={autoAssignMutation.isPending || unassignedDelegates.length === 0} data-testid="confirm-auto-assign-btn">
              {autoAssignMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Auto-Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Quick-Assign Dialog */}
      <Dialog open={showManualAssignDialog} onOpenChange={setShowManualAssignDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-500" />
              Quick Manual Assignment
            </DialogTitle>
            <DialogDescription>Select a delegate and assign them to a specific room.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Delegate</Label>
              <Select value={manualAssignDelegate} onValueChange={setManualAssignDelegate}>
                <SelectTrigger data-testid="select-delegate-dropdown"><SelectValue placeholder="Choose a delegate..." /></SelectTrigger>
                <SelectContent>
                  {unassignedDelegates.map(delegate => (
                    <SelectItem key={delegate.id} value={delegate.id}>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{delegate.name || `${delegate.first_name} ${delegate.last_name}`}</span>
                        {delegate.is_vip && <Star className="h-3 w-3 text-amber-500" />}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select Room</Label>
              <Select value={manualAssignUnit} onValueChange={setManualAssignUnit}>
                <SelectTrigger data-testid="select-room-dropdown"><SelectValue placeholder="Choose a room..." /></SelectTrigger>
                <SelectContent>
                  {units.map(unit => {
                    const roomType = getRoomType(unit.type);
                    const occupancy = getAssignedDelegates(unit.id).length;
                    const isFull = occupancy >= roomType.capacity;
                    return (
                      <SelectItem key={unit.id} value={unit.id} disabled={isFull}>
                        <div className="flex items-center justify-between w-full">
                          <span>{unit.name}</span>
                          <Badge variant={isFull ? 'secondary' : 'outline'} className="ml-2">{occupancy}/{roomType.capacity}</Badge>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleManualAssign} disabled={!manualAssignUnit || !manualAssignDelegate || assignDelegateMutation.isPending} data-testid="confirm-manual-assign-btn">
              {assignDelegateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Assign Results Dialog */}
      <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Auto-Assignment Results</DialogTitle></DialogHeader>
          {autoAssignResults && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-green-700">{autoAssignResults.assigned?.length || 0}</div>
                  <div className="text-xs text-green-600">Assigned</div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-500 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-red-700">{autoAssignResults.failed?.length || 0}</div>
                  <div className="text-xs text-red-600">Failed</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-slate-500 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-slate-700">{autoAssignResults.skipped?.length || 0}</div>
                  <div className="text-xs text-slate-600">Skipped</div>
                </div>
              </div>
              {autoAssignResults.failed?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-red-600">Failed Assignments:</Label>
                  <ScrollArea className="h-24 border rounded-lg p-2">
                    {autoAssignResults.failed.map((item, i) => (
                      <div key={i} className="text-sm py-1 border-b last:border-0">
                        <span className="font-medium">{item.delegate_name}</span>
                        <span className="text-red-500 ml-2">- {item.reason}</span>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}
              {autoAssignResults.skipped?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-600">Skipped:</Label>
                  <ScrollArea className="h-24 border rounded-lg p-2">
                    {autoAssignResults.skipped.map((item, i) => (
                      <div key={i} className="text-sm py-1 border-b last:border-0">
                        <span className="font-medium">{item.delegate_name}</span>
                        <span className="text-slate-500 ml-2">- {item.reason}</span>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={() => setShowResultsDialog(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Dialog */}
      <Dialog open={!!editingUnit} onOpenChange={(open) => !open && setEditingUnit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Edit Housing Unit
            </DialogTitle>
          </DialogHeader>
          {editingUnit && (
            <div className="space-y-4">
              <div>
                <Label>Unit Name</Label>
                <Input
                  value={editingUnit.name || ''}
                  onChange={(e) => setEditingUnit({...editingUnit, name: e.target.value})}
                  placeholder="Room 101"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Building</Label>
                  <Input
                    value={editingUnit.building || ''}
                    onChange={(e) => setEditingUnit({...editingUnit, building: e.target.value})}
                    placeholder="Building A"
                  />
                </div>
                <div>
                  <Label>Floor</Label>
                  <Input
                    value={editingUnit.floor || ''}
                    onChange={(e) => setEditingUnit({...editingUnit, floor: e.target.value})}
                    placeholder="1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select 
                    value={editingUnit.type || 'double'} 
                    onValueChange={(v) => setEditingUnit({...editingUnit, type: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="double">Double</SelectItem>
                      <SelectItem value="quad">Quad</SelectItem>
                      <SelectItem value="suite">Suite</SelectItem>
                      <SelectItem value="dorm">Dorm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Capacity</Label>
                  <Input
                    type="number"
                    value={editingUnit.capacity || 2}
                    onChange={(e) => setEditingUnit({...editingUnit, capacity: parseInt(e.target.value) || 2})}
                    min={1}
                    max={20}
                  />
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select 
                  value={editingUnit.status || 'available'} 
                  onValueChange={(v) => setEditingUnit({...editingUnit, status: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="occupied">Occupied</SelectItem>
                    <SelectItem value="dirty">Dirty</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editingUnit.notes || ''}
                  onChange={(e) => setEditingUnit({...editingUnit, notes: e.target.value})}
                  placeholder="Any additional notes..."
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUnit(null)}>Cancel</Button>
            <Button 
              onClick={() => {
                updateUnitMutation.mutate({
                  unitId: editingUnit.id,
                  data: {
                    name: editingUnit.name,
                    building: editingUnit.building,
                    floor: editingUnit.floor,
                    type: editingUnit.type,
                    capacity: editingUnit.capacity,
                    status: editingUnit.status,
                    notes: editingUnit.notes
                  }
                });
              }}
              disabled={updateUnitMutation.isPending}
            >
              {updateUnitMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HousingAssignment;
